/**
 * Top-down 2D minimap overlay for the Silva City 3D map.
 *
 * Renders the irregular site boundary, road network, plot clusters,
 * and amenity hotspots as an SVG inside a glassmorphic chip. Clicking
 * a hotspot fires `onSelect(id)`, and the optional `activeId` is
 * highlighted with a gold ring.
 */

import React from "react";
import { motion } from "framer-motion";
import {
  BOUNDARY_PDF,
  HOTSPOTS,
  PLOT_CLUSTERS_PDF,
  ROADS_PDF,
  MOUNTAIN_RIDGES_PDF,
} from "./silvaCityLayout";

const VB = 110; // 100 + 5 padding each side

const pathFromPdf = (points, close = false) => {
  const pts = points.map(([x, y]) => `${x + 5},${y + 5}`).join(" L ");
  return `M ${pts}${close ? " Z" : ""}`;
};

const ProjectMinimap = ({ activeId = null, onSelect, cameraTarget = null }) => {
  return (
    <div
      data-testid="map-minimap"
      className="pointer-events-auto rounded-2xl border border-white/10 glass p-2.5 shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-xl w-[180px] md:w-[210px]"
    >
      <div className="flex items-center justify-between mb-2 px-1">
        <p className="text-[9px] tracking-[0.28em] uppercase text-white/55">Minimap</p>
        <span className="text-[9px] tracking-[0.24em] uppercase text-[color:var(--sp-gold)]">N&nbsp;↑</span>
      </div>
      <svg
        viewBox={`0 0 ${VB} ${VB}`}
        className="block w-full h-auto"
        aria-label="Silva City minimap"
      >
        <defs>
          <linearGradient id="mm-ground" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1a1f15" />
            <stop offset="100%" stopColor="#101410" />
          </linearGradient>
          <linearGradient id="mm-mountain" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(4,99,7,0.45)" />
            <stop offset="100%" stopColor="rgba(4,99,7,0.15)" />
          </linearGradient>
          <radialGradient id="mm-pulse" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(212,175,55,0.7)" />
            <stop offset="100%" stopColor="rgba(212,175,55,0)" />
          </radialGradient>
        </defs>

        {/* Mountain ridges as soft polylines */}
        {MOUNTAIN_RIDGES_PDF.map((line, i) => (
          <path
            key={`mm-mtn-${i}`}
            d={pathFromPdf(line)}
            fill="none"
            stroke="url(#mm-mountain)"
            strokeWidth="3.2"
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity="0.75"
          />
        ))}

        {/* Site boundary */}
        <path
          d={pathFromPdf(BOUNDARY_PDF, true)}
          fill="url(#mm-ground)"
          stroke="rgba(212,175,55,0.55)"
          strokeWidth="0.7"
          strokeLinejoin="round"
        />

        {/* Plot clusters as subtle filled rects */}
        {PLOT_CLUSTERS_PDF.map((c) => {
          const [x1, y1, x2, y2] = c.corners;
          return (
            <rect
              key={`mm-pc-${c.id}`}
              x={x1 + 5}
              y={y1 + 5}
              width={x2 - x1}
              height={y2 - y1}
              fill="rgba(212,175,55,0.06)"
              stroke="rgba(212,175,55,0.2)"
              strokeWidth="0.25"
            />
          );
        })}

        {/* Road network */}
        {ROADS_PDF.map((r) => (
          <path
            key={`mm-r-${r.id}`}
            d={pathFromPdf(r.points)}
            fill="none"
            stroke={r.id === "spine" ? "rgba(212,175,55,0.75)" : "rgba(255,255,255,0.35)"}
            strokeWidth={r.id === "spine" ? 0.9 : 0.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {/* Hotspots */}
        {HOTSPOTS.map((h) => {
          const [px, py] = h.pdfPos;
          const isActive = activeId === h.id;
          const fill =
            h.color === "emerald" ? "rgba(4,99,7,1)" : "rgba(212,175,55,1)";
          return (
            <g
              key={`mm-h-${h.id}`}
              onClick={() => onSelect && onSelect(h.id)}
              className="cursor-pointer"
              data-testid={`minimap-hotspot-${h.id}`}
            >
              {isActive && (
                <circle cx={px + 5} cy={py + 5} r="6" fill="url(#mm-pulse)">
                  <animate
                    attributeName="r"
                    values="3;7;3"
                    dur="2.4s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}
              <circle
                cx={px + 5}
                cy={py + 5}
                r={isActive ? 2.4 : 1.7}
                fill={fill}
                stroke="#fff"
                strokeWidth="0.35"
              />
            </g>
          );
        })}

        {/* Live camera target dot */}
        {cameraTarget && (
          <g>
            <circle
              cx={cameraTarget[0] + 5}
              cy={cameraTarget[1] + 5}
              r="1.4"
              fill="#fff"
              opacity="0.85"
            />
            <circle
              cx={cameraTarget[0] + 5}
              cy={cameraTarget[1] + 5}
              r="3"
              fill="none"
              stroke="rgba(255,255,255,0.45)"
              strokeWidth="0.35"
              strokeDasharray="0.6 0.8"
            >
              <animateTransform
                attributeName="transform"
                type="rotate"
                from={`0 ${cameraTarget[0] + 5} ${cameraTarget[1] + 5}`}
                to={`360 ${cameraTarget[0] + 5} ${cameraTarget[1] + 5}`}
                dur="6s"
                repeatCount="indefinite"
              />
            </circle>
          </g>
        )}
      </svg>

      <motion.p
        initial={{ opacity: 0.5 }}
        animate={{ opacity: [0.4, 0.85, 0.4] }}
        transition={{ duration: 2.4, repeat: Infinity }}
        className="mt-2 px-1 text-[8.5px] tracking-[0.22em] uppercase text-white/55 text-center"
      >
        Tap a pin to focus
      </motion.p>
    </div>
  );
};

export default ProjectMinimap;
